import { useCallback, useEffect, useRef, useState } from 'react';
import { nowHHMM } from './format.js';

/**
 * Returns the current local time as "HH:MM" and re-renders once a minute so
 * time-sensitive UI (e.g. hiding same-day slots that have just passed) stays
 * current while the screen is open. Ticks are aligned to the minute boundary
 * so the value flips exactly when the clock does.
 */
export function useNowMinute() {
  const [now, setNow] = useState(nowHHMM);
  useEffect(() => {
    let timer;
    const schedule = () => {
      const msToNextMinute = 60000 - (Date.now() % 60000);
      timer = setTimeout(() => {
        setNow(nowHHMM());
        schedule();
      }, msToNextMinute + 50);
    };
    schedule();
    // Catch up immediately when the tab regains focus after being backgrounded.
    const onVisible = () => document.visibilityState === 'visible' && setNow(nowHHMM());
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  return now;
}

/**
 * Polls an endpoint and keeps the UI in sync without a manual refresh.
 * `onChange(next, prev)` fires only when the payload actually differs, which is
 * what drives the "your nurse just accepted" style toasts.
 */
export function useLive(fetcher, { interval = 15000, deps = [], onChange, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const previous = useRef(null);
  const changeRef = useRef(onChange);
  const fetchRef = useRef(fetcher);
  changeRef.current = onChange;
  fetchRef.current = fetcher;

  const run = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const next = await fetchRef.current();
      setError(null);
      setStale(false);
      setData((current) => {
        const serialised = JSON.stringify(next);
        if (serialised === JSON.stringify(current)) return current;
        if (previous.current !== null) changeRef.current?.(next, previous.current);
        previous.current = next;
        return next;
      });
      if (previous.current === null) previous.current = next;
      return next;
    } catch (err) {
      setError(err);
      // A background poll failing shouldn't blank the screen — flag it instead.
      if (quiet) setStale(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    previous.current = null;
    run();

    const tick = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) run({ quiet: true });
    };
    const timer = setInterval(tick, interval);
    // Catch up immediately when the user returns to the tab.
    const onVisible = () => document.visibilityState === 'visible' && run({ quiet: true });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, interval, enabled, ...deps]);

  return { data, setData, loading, error, stale, refresh: () => run({ quiet: true }) };
}

/** Delays a fast-changing value — used for search-as-you-type. */
export function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const THEME_KEY = 'healathome.theme';

/** Theme preference with a 'system' option that follows the OS. */
export function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'system');

  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia('(prefers-color-scheme: dark)');

    const apply = () => {
      const resolved = theme === 'system' ? (media.matches ? 'dark' : 'light') : theme;
      root.dataset.theme = resolved;
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', resolved === 'dark' ? '#042f2e' : '#0f766e');
    };

    apply();
    if (theme === 'system') {
      media.addEventListener('change', apply);
      return () => media.removeEventListener('change', apply);
    }
    return undefined;
  }, [theme]);

  const change = useCallback((next) => {
    localStorage.setItem(THEME_KEY, next);
    setTheme(next);
  }, []);

  return [theme, change];
}

/** True once the element scrolls into view — used for entrance animations. */
export function useInView(options = { threshold: 0.12 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return setInView(true);
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, inView];
}

/** Confirms an action inline: first click arms, second click commits. */
export function useArmed(timeout = 4000) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), timeout);
    return () => clearTimeout(t);
  }, [armed, timeout]);
  return [armed, setArmed];
}
